import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../../../../context/UserContext";
import { axiosInstance } from "../../../../utils/networking";
import { EventType } from "../../../../interfaces";
import { ContentStepProps } from "../broadcastTypes";
import dayjs from "dayjs";
import { EventInvitation } from "../../../common/EmailBodies/EventInvitation";
import { Editor } from "@tinymce/tinymce-react";
import "tinymce/tinymce";
import "tinymce/themes/silver";
import "tinymce/icons/default";
import "tinymce/models/dom";
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/table";
import "tinymce/plugins/help";

const EventInvitationContent = ({
  contentData,
  setContentData,
}: ContentStepProps) => {
  const { user } = useContext(UserContext);
  const [events, setEvents] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState<string>(
    contentData.eventId || "",
  );
  const editorRef = useRef<any>(null);
  // Stable ref so initialValue doesn't change between re-renders while typing
  const initialHtmlRef = useRef<string>(contentData.htmlContent);
  // Stable ref to avoid stale closure in onEditorChange
  const contentDataRef = useRef<typeof contentData>(contentData);
  useEffect(() => {
    contentDataRef.current = contentData;
  }, [contentData]);

  useEffect(() => {
    axiosInstance
      .get("/events")
      .then((res) => {
        setEvents(res.data.events || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleEventChange = (eventId: string) => {
    setSelectedEventId(eventId);
    const event = events.find((e) => e._id?.toString() === eventId);
    if (!event) return;

    const subject = `You're Invited: ${event.name.text}`;
    const htmlContent = EventInvitation(
      user,
      null,
      null,
      null,
      null,
      null,
      event,
    );
    initialHtmlRef.current = htmlContent;

    setContentData({
      subject,
      htmlContent,
      eventId,
    });
  };

  const handleSubjectChange = (subject: string) => {
    setContentData({ ...contentData, subject });
  };

  const selectedEvent = events.find((e) => e._id === selectedEventId);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        No upcoming events found. Please create an event first.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
      <FormControl fullWidth>
        <InputLabel>Select Event</InputLabel>
        <Select
          value={selectedEventId}
          label="Select Event"
          onChange={(e) => handleEventChange(e.target.value)}
        >
          {events.map((event) => (
            <MenuItem key={event._id?.toString()} value={event._id?.toString()}>
              {event.name.text} —{" "}
              {dayjs(event.start?.local).format("MMM D, YYYY")}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedEvent && (
        <Alert
          severity="info"
          icon={false}
          sx={{ textAlign: "center", "& .MuiAlert-message": { width: "100%" } }}
        >
          <Typography variant="body2">
            <strong>{selectedEvent.name.text}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedEvent.locationInfo?.localized_address_display}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {dayjs(selectedEvent.start?.local).format("MMMM D, YYYY h:mm A")}
            {" - "}
            {dayjs(selectedEvent.end?.local).format("h:mm A")}
          </Typography>
        </Alert>
      )}

      <TextField
        label="Email Subject"
        fullWidth
        value={contentData.subject}
        onChange={(e) => handleSubjectChange(e.target.value)}
        placeholder="You're Invited: Event Name"
      />

      {selectedEventId && (
        <>
          <Typography variant="body2" color="text.secondary">
            Preview and edit the email below before sending:
          </Typography>
          <Box
            sx={{
              border: "1px solid #c4c4c4",
              borderRadius: "0 0 10px 10px",
              height: 500,
            }}
          >
            <Editor
              onInit={(_evt: any, editor: any) => {
                editorRef.current = editor;
              }}
              initialValue={initialHtmlRef.current}
              key={selectedEventId}
              init={{
                licenseKey: "gpl",
                skin_url: "/skins/ui/oxide",
                content_css: "/skins/content/default/content.css",
                height: 500,
                menubar: false,
                toolbar:
                  "undo redo | formatselect | " +
                  "bold italic backcolor | alignleft aligncenter " +
                  "alignright alignjustify | bullist numlist outdent indent | " +
                  "removeformat | help",
                content_style:
                  "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                relative_urls: false,
                remove_script_host: false,
                convert_urls: true,
                setup: (editor: any) => {
                  editor.on("blur", () => {
                    setContentData({
                      ...contentDataRef.current,
                      htmlContent: editor.getContent(),
                    });
                  });
                },
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default EventInvitationContent;
